import { useCallback, useEffect, useState } from 'react';
import { useSearchParams } from 'react-router';
import { Alert, Button } from '@/components/ui';
import { api } from '@/lib/api';
import { errorMessage } from '@/lib/errors';

export interface ContaAzulStatus {
  configurado: boolean;
  conectado: boolean;
  conectadoPor: string | null;
  conectadoEm: string | null;
  ultimoSucessoEm: string | null;
  ultimaSincronizacao: {
    status: 'EM_ANDAMENTO' | 'SUCESSO' | 'FALHA';
    iniciadoEm: string;
    finalizadoEm: string | null;
    vendasNovas: number;
    vendasAtualizadas: number;
    vendasCanceladas: number;
    vendasExcluidas: number;
    erro: string | null;
  } | null;
}

export function formatDateTime(iso: string | null): string {
  if (!iso) return '—';
  return new Intl.DateTimeFormat('pt-BR', { dateStyle: 'short', timeStyle: 'medium', timeZone: 'America/Bahia' }).format(new Date(iso));
}

const RETORNO: Record<string, { kind: 'success' | 'error' | 'warning'; text: string }> = {
  conectado: { kind: 'success', text: 'Conta Azul conectado! As vendas começam a chegar em instantes.' },
  cancelado: { kind: 'warning', text: 'A conexão foi cancelada no Conta Azul. Nada foi alterado.' },
  erro: { kind: 'error', text: 'Não foi possível concluir a conexão. Clique em "Conectar" e tente de novo.' },
};

/** Tela do administrador para ligar o sistema ao Conta Azul e acompanhar a sincronização. */
export function IntegrationsPage() {
  const [params, setParams] = useSearchParams();
  const [status, setStatus] = useState<ContaAzulStatus | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [syncing, setSyncing] = useState(false);
  const retorno = RETORNO[params.get('conta_azul') ?? ''];

  const load = useCallback(async () => {
    try {
      setStatus(await api.get<ContaAzulStatus>('/integrations/conta-azul/status'));
    } catch (err) {
      setError(errorMessage(err));
    }
  }, []);

  useEffect(() => {
    void load();
    const t = window.setInterval(() => void load(), 10_000);
    return () => window.clearInterval(t);
  }, [load]);

  async function sync() {
    setError(null);
    setNotice(null);
    setSyncing(true);
    try {
      const r = await api.post<{ vendasNovas: number; vendasAtualizadas: number; status: string }>('/integrations/conta-azul/sync');
      setNotice(r.status === 'FALHA' ? 'A atualização falhou. Veja o detalhe abaixo.' : `Atualizado: ${r.vendasNovas} venda(s) nova(s), ${r.vendasAtualizadas} alterada(s).`);
      await load();
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      setSyncing(false);
    }
  }

  async function disconnect() {
    if (!window.confirm('Desconectar o Conta Azul? As vendas já importadas continuam no sistema, mas novas vendas param de chegar.')) return;
    try {
      await api.delete('/integrations/conta-azul');
      await load();
    } catch (err) {
      setError(errorMessage(err));
    }
  }

  const ultima = status?.ultimaSincronizacao;

  return (
    <div className="page">
      <header className="page-header">
        <div>
          <h1 className="page-title">Integrações</h1>
          <p className="muted">As vendas vêm do Conta Azul automaticamente, a cada minuto.</p>
        </div>
      </header>

      {retorno && (
        <div className="row">
          <Alert kind={retorno.kind}>{retorno.text}</Alert>
          <Button variant="ghost" onClick={() => setParams({})}>
            Fechar aviso
          </Button>
        </div>
      )}
      {error && <Alert>{error}</Alert>}
      {notice && <Alert kind="success">{notice}</Alert>}

      <section className="card">
        <div className="integration">
          <div className="integration__logo" aria-hidden="true">CA</div>
          <div className="integration__info">
            <h2>Conta Azul</h2>
            {!status ? (
              <p className="muted">Carregando…</p>
            ) : !status.configurado ? (
              <p className="muted">As credenciais do Conta Azul ainda não foram configuradas no servidor.</p>
            ) : status.conectado ? (
              <p className="muted">
                <span className="badge badge--ok">Conectado</span> por {status.conectadoPor ?? '—'} em {formatDateTime(status.conectadoEm)}
              </p>
            ) : (
              <p className="muted">
                <span className="badge badge--warn">Não conectado</span> Clique em conectar e entre com a conta da agência no Conta Azul.
              </p>
            )}
          </div>
          <div className="row">
            {status?.configurado && !status.conectado && (
              <a className="btn btn--primary" href="/api/integrations/conta-azul/connect">
                Conectar Conta Azul
              </a>
            )}
            {status?.conectado && (
              <>
                <Button onClick={() => void sync()} loading={syncing}>
                  Sincronizar agora
                </Button>
                <Button variant="danger" onClick={() => void disconnect()}>
                  Desconectar
                </Button>
              </>
            )}
          </div>
        </div>
      </section>

      {status?.conectado && (
        <section className="card">
          <h2>Última sincronização</h2>
          {!ultima ? (
            <p className="muted">Ainda não houve sincronização.</p>
          ) : (
            <>
              <dl className="stats">
                <div>
                  <dt>Situação</dt>
                  <dd>
                    {ultima.status === 'SUCESSO' && <span className="badge badge--ok">Concluída</span>}
                    {ultima.status === 'EM_ANDAMENTO' && <span className="badge badge--warn">Em andamento</span>}
                    {ultima.status === 'FALHA' && <span className="badge badge--off">Falhou</span>}
                  </dd>
                </div>
                <div>
                  <dt>Quando</dt>
                  <dd>{formatDateTime(ultima.finalizadoEm ?? ultima.iniciadoEm)}</dd>
                </div>
                <div>
                  <dt>Novas</dt>
                  <dd>{ultima.vendasNovas}</dd>
                </div>
                <div>
                  <dt>Alteradas</dt>
                  <dd>{ultima.vendasAtualizadas}</dd>
                </div>
                <div>
                  <dt>Canceladas</dt>
                  <dd>{ultima.vendasCanceladas}</dd>
                </div>
                <div>
                  <dt>Excluídas</dt>
                  <dd>{ultima.vendasExcluidas}</dd>
                </div>
              </dl>
              {ultima.erro && <Alert kind="warning">{ultima.erro}</Alert>}
              <p className="muted small">Último sucesso: {formatDateTime(status.ultimoSucessoEm)}</p>
            </>
          )}
        </section>
      )}

      <DigisacCard />
      <ServicosCard />
      <MercadoPagoCard />
    </div>
  );
}

interface DigisacStatus {
  configurado: boolean;
  webhookAtivo: boolean | null;
  ultimoEventoEm?: string;
  ultimaImportacaoEm?: string;
  importados?: number;
  clientes: number;
  arquivos: number;
  atendentesLigados: number;
}

/** Digisac: webhook de mensagens, importação inicial e situação. */
function DigisacCard() {
  const [st, setSt] = useState<DigisacStatus | null>(null);
  const [erro, setErro] = useState<string | null>(null);
  const [aviso, setAviso] = useState<string | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const load = useCallback(async () => {
    try {
      setSt(await api.get<DigisacStatus>('/integrations/digisac/status'));
    } catch (err) {
      setErro(errorMessage(err));
    }
  }, []);
  useEffect(() => {
    void load();
  }, [load]);

  async function run(acao: string, fn: () => Promise<string>) {
    setBusy(acao);
    setErro(null);
    setAviso(null);
    try {
      setAviso(await fn());
      await load();
    } catch (err) {
      setErro(errorMessage(err));
    } finally {
      setBusy(null);
    }
  }

  return (
    <section className="card">
      <div className="integration">
        <div className="integration__logo integration__logo--digisac" aria-hidden="true">
          DS
        </div>
        <div className="integration__info">
          <h2>Digisac</h2>
          {!st ? (
            <p className="muted">Carregando…</p>
          ) : !st.configurado ? (
            <p className="muted">O token da Digisac ainda não foi configurado no servidor.</p>
          ) : (
            <p className="muted">
              {st.webhookAtivo ? <span className="badge badge--ok">Recebendo mensagens</span> : <span className="badge badge--warn">Webhook não ativado</span>} {st.clientes} clientes
              · {st.arquivos} arquivos salvos · {st.atendentesLigados} atendente(s) ligado(s)
              {st.ultimoEventoEm && <> · última mensagem {formatDateTime(st.ultimoEventoEm)}</>}
            </p>
          )}
        </div>
        {st?.configurado && (
          <div className="row">
            {!st.webhookAtivo && (
              <Button
                loading={busy === 'setup'}
                onClick={() =>
                  void run('setup', async () => {
                    const r = await api.post<{ criado: boolean }>('/integrations/digisac/setup');
                    return r.criado ? 'Webhook ativado: as mensagens novas já chegam no CRM.' : 'O webhook já estava ativo.';
                  })
                }
              >
                Ativar recebimento
              </Button>
            )}
            <Button
              variant="secondary"
              loading={busy === 'import'}
              onClick={() =>
                void run('import', async () => {
                  const r = await api.post<{ chamados: number; novos: number }>('/integrations/digisac/import', { dias: 2 });
                  return `Verificação concluída: ${r.chamados} atendimentos lidos, ${r.novos} cliente(s) novo(s) no funil.`;
                })
              }
            >
              Buscar atendimentos de hoje
            </Button>
          </div>
        )}
      </div>
      {erro && <Alert>{erro}</Alert>}
      {aviso && <Alert kind="success">{aviso}</Alert>}
      {st?.configurado && st.atendentesLigados === 0 && (
        <Alert kind="warning">Ligue cada usuário ao atendente dele na Digisac (tela Usuários). Sem isso, os clientes ficam sem consultor.</Alert>
      )}
    </section>
  );
}

/** IA e e-mail: mostram se as chaves foram configuradas no servidor. */
function ServicosCard() {
  const [caps, setCaps] = useState<{ ia: boolean; email: boolean } | null>(null);
  useEffect(() => {
    void api.get<{ ia: boolean; email: boolean }>('/crm/capabilities').then(setCaps).catch(() => setCaps(null));
  }, []);
  if (!caps) return null;
  return (
    <section className="card">
      <h2>IA e e-mail</h2>
      <p className="muted">
        IA (Claude): {caps.ia ? <span className="badge badge--ok">Ligada</span> : <span className="badge badge--warn">Falta a chave ANTHROPIC_API_KEY</span>}
        {' · '}
        E-mail (Resend): {caps.email ? <span className="badge badge--ok">Ligado</span> : <span className="badge badge--warn">Falta a chave RESEND_API_KEY</span>}
      </p>
    </section>
  );
}

/** Conta do Conta Azul onde entram os recebimentos dos links do Mercado Pago. */
function MercadoPagoCard() {
  const [cfg, setCfg] = useState<{ conta: { id: string; nome: string } | null; contas: Array<{ id: string; nome: string; tipo: string | null }> } | null>(null);
  const [sel, setSel] = useState('');
  const [msg, setMsg] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    void api
      .get<NonNullable<typeof cfg>>('/integrations/mercadopago/config')
      .then((c) => {
        setCfg(c);
        setSel(c.conta?.id ?? '');
      })
      .catch(() => setCfg(null));
  }, []);
  if (!cfg) return null;
  async function salvar() {
    if (!sel) return;
    setBusy(true);
    setMsg(null);
    try {
      const conta = await api.put<{ id: string; nome: string }>('/integrations/mercadopago/config', { contaId: sel });
      setCfg((c) => (c ? { ...c, conta } : c));
      setMsg('Conta salva. As próximas baixas entram nela.');
    } catch (e) {
      setMsg(errorMessage(e));
    } finally {
      setBusy(false);
    }
  }
  return (
    <section className="card">
      <h2>Mercado Pago → Conta Azul</h2>
      <p className="muted">
        Quando um link pago está ligado a uma venda, o sistema registra o recebimento (baixa) da parcela no Conta Azul, nesta conta financeira.{' '}
        {cfg.conta ? <span className="badge badge--ok">{cfg.conta.nome}</span> : <span className="badge badge--warn">Escolha a conta</span>}
      </p>
      <div className="row tight">
        <select value={sel} onChange={(e) => setSel(e.target.value)} aria-label="Conta financeira do Mercado Pago">
          <option value="">Escolha a conta…</option>
          {cfg.contas.map((c) => (
            <option key={c.id} value={c.id}>
              {c.nome}
            </option>
          ))}
        </select>
        <Button loading={busy} onClick={() => void salvar()}>
          Salvar
        </Button>
      </div>
      {msg && <p className="muted small">{msg}</p>}
    </section>
  );
}
